import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Plus, Calendar, Upload, X, ClipboardList, Search, Filter, Eye, Clock, Users, Image, CheckCircle2, CircleAlert, FileText, Pencil, Trash2, ClipboardCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const WorkReports = () => {
  const { user } = useContext(AuthContext);
  console.log("USER:", user);
  console.log("PERMISSIONS:", user?.permissions);
  const [reports, setReports] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
  new Date().toISOString().split("T")[0]
);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [savingReport, setSavingReport] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    work_description: '',
    tools_used: '',
    materials_used: '',
    observations: ''
  });
  const [uploadedImages, setUploadedImages] = useState([]);
const [activities, setActivities] = useState([]);
const [workday, setWorkday] = useState({
  morning_start: '',
  morning_end: '',
  afternoon_start: '',
  afternoon_end: ''
});
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
const [reviewData, setReviewData] = useState({
  reportId: "",
  comment: ""
});

const [expandedReport, setExpandedReport] = useState(null);
const [viewingReport, setViewingReport] = useState(null);
const [expandedDate, setExpandedDate] = useState({});

const [searchTerm, setSearchTerm] = useState('');
const [workerFilter, setWorkerFilter] = useState('');
const [statusFilter, setStatusFilter] = useState('all');
const [dateFromFilter, setDateFromFilter] = useState('');
const [dateToFilter, setDateToFilter] = useState('');

const filteredReports = reports.filter((report) => {
  const search = searchTerm.trim().toLowerCase();

  const matchesSearch =
    !search ||
    report.worker_name?.toLowerCase().includes(search) ||
    report.work_description?.toLowerCase().includes(search) ||
    report.activities?.some((activity) =>
      activity.description?.toLowerCase().includes(search)
    );

  const matchesWorker =
    !workerFilter || report.worker_id === workerFilter;

  const matchesStatus =
    statusFilter === 'all' ||
    (statusFilter === 'reviewed' && report.reviewed) ||
    (statusFilter === 'pending' && !report.reviewed);

  const matchesDateFrom =
    !dateFromFilter || report.date >= dateFromFilter;

  const matchesDateTo =
    !dateToFilter || report.date <= dateToFilter;

  return (
    matchesSearch &&
    matchesWorker &&
    matchesStatus &&
    matchesDateFrom &&
    matchesDateTo
  );
});

const filteredWorkers = [
  ...new Map(
    reports
      .filter((report) => report.worker_id)
      .map((report) => [
        report.worker_id,
        report.worker_name
      ])
  ).entries()
].map(([id, name]) => ({ id, name }));

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/work-reports`);
      console.log("REPORTES API:", response.data);
      setReports(response.data);
    } catch (error) {
      setSavingReport(false);
      toast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const calculateActivityDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;

  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  const start = startHour * 60 + startMinute;
  const end = endHour * 60 + endMinute;

  if (end <= start) return 0;

  return end - start;
};

const addActivity = () => {
  setActivities([
    ...activities,
    {
      id: Date.now(),
      description: '',
      start_time: '',
      end_time: '',
      duration_minutes: 0
    }
  ]);
};

const updateActivity = (id, field, value) => {
  setActivities(
    activities.map((activity) => {
      if (activity.id !== id) return activity;

      const updated = {
        ...activity,
        [field]: value
      };

      if (field === 'start_time' || field === 'end_time') {
        updated.duration_minutes = calculateActivityDuration(
          updated.start_time,
          updated.end_time
        );
      }

      return updated;
    })
  );
};

const removeActivity = (id) => {
  setActivities(activities.filter((activity) => activity.id !== id));
};

const getTotalActivityMinutes = () => {
  return activities.reduce(
    (total, activity) => total + (activity.duration_minutes || 0),
    0
  );
};

const formatDuration = (minutes) => {
  if (!minutes) return '0 min';

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  if (hours === 0) return `${remainingMinutes} min`;
  if (remainingMinutes === 0) return `${hours} h`;

  return `${hours} h ${remainingMinutes} min`;
};

const calculateWorkdayRange = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;

  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  const start = startHour * 60 + startMinute;
  const end = endHour * 60 + endMinute;

  if (end <= start) return 0;

  return end - start;
};

const getMorningMinutes = () => {
  return calculateWorkdayRange(
    workday.morning_start,
    workday.morning_end
  );
};

const getAfternoonMinutes = () => {
  return calculateWorkdayRange(
    workday.afternoon_start,
    workday.afternoon_end
  );
};

const getWorkdayMinutes = () => {
  return getMorningMinutes() + getAfternoonMinutes();
};

const updateWorkday = (field, value) => {
  setWorkday({
    ...workday,
    [field]: value
  });
};

const exportWorkReport = async (report) => {
    try {
      const doc = new jsPDF("p", "mm", "a4");

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 14;
      const contentWidth = pageWidth - margin * 2;

      const orange = [231, 105, 27];
      const dark = [38, 50, 65];
      const gray = [95, 105, 115];
      const light = [245, 247, 249];
      const border = [220, 225, 230];
      const white = [255, 255, 255];
      const green = [35, 150, 85];

      const activities = Array.isArray(report.activities)
        ? report.activities
        : [];

      const images = Array.isArray(report.images)
        ? report.images
        : [];

      const workdayMinutes =
        calculateWorkdayRange(
          report.morning_start,
          report.morning_end
        ) +
        calculateWorkdayRange(
          report.afternoon_start,
          report.afternoon_end
        );

      const activityMinutes =
        activities.reduce(
          (total, activity) =>
            total + Number(activity.duration_minutes || 0),
          0
        );

      const unassignedMinutes = Math.max(
        0,
        workdayMinutes - activityMinutes
      );

      const effectiveness =
        workdayMinutes > 0
          ? Math.round(
              (activityMinutes / workdayMinutes) * 100
            )
          : 0;

      const formatDate = (dateValue) => {
        if (!dateValue) return "-";

        const parts = String(dateValue).split("-");

        if (parts.length === 3) {
          return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }

        return dateValue;
      };

      const formatTime = (value) => {
        if (!value) return "--:--";
        return String(value).substring(0, 5);
      };

      const drawHeader = () => {
        doc.setFillColor(...white);
        doc.rect(
          0,
          0,
          pageWidth,
          pageHeight,
          "F"
        );

        doc.setFillColor(...orange);
        doc.rect(
          0,
          0,
          pageWidth,
          4,
          "F"
        );

        doc.setTextColor(...dark);
        doc.setFont(
          "helvetica",
          "bold"
        );
        doc.setFontSize(19);

        doc.text(
          "MAQ",
          margin,
          15
        );

        doc.setTextColor(...orange);

        doc.text(
          "CONTROL",
          margin + 22,
          15
        );

        doc.setTextColor(...dark);
        doc.setFont(
          "helvetica",
          "normal"
        );
        doc.setFontSize(6.8);

        doc.text(
          "GESTION DE OPERACIONES Y SERVICIOS",
          margin,
          20
        );

        doc.setTextColor(...dark);
        doc.setFont(
          "helvetica",
          "bold"
        );
        doc.setFontSize(13);

        doc.text(
          "REPORTE DE TRABAJO",
          pageWidth - margin,
          13,
          { align: "right" }
        );

        doc.setTextColor(...orange);
        doc.setFontSize(7.5);

        doc.text(
          "Registro diario de actividades",
          pageWidth - margin,
          18,
          { align: "right" }
        );

        doc.setDrawColor(...orange);
        doc.setLineWidth(1);

        doc.line(
          margin,
          25,
          pageWidth - margin,
          25
        );
      };

      const drawFooter = () => {
        const footerY =
          pageHeight - 10;

        doc.setDrawColor(...orange);
        doc.setLineWidth(0.7);

        doc.line(
          margin,
          footerY - 4,
          pageWidth - margin,
          footerY - 4
        );

        doc.setTextColor(...dark);
        doc.setFont(
          "helvetica",
          "bold"
        );
        doc.setFontSize(6.5);

        doc.text(
          "MAQControl",
          margin,
          footerY
        );

        doc.setFont(
          "helvetica",
          "normal"
        );

        doc.text(
          " - Reporte de Trabajo",
          margin + 22,
          footerY
        );
      };

      const addPage = () => {
        doc.addPage();
        drawHeader();
      };

      const sectionTitle = (
        number,
        title,
        y
      ) => {
        const height = 8;

        doc.setFillColor(...dark);

        doc.roundedRect(
          margin,
          y,
          contentWidth,
          height,
          1.5,
          1.5,
          "F"
        );

        doc.setFillColor(...orange);

        doc.roundedRect(
          margin,
          y,
          9,
          height,
          1.5,
          1.5,
          "F"
        );

        doc.setFillColor(...orange);

        doc.rect(
          margin + 7,
          y,
          2,
          height,
          "F"
        );

        doc.setTextColor(...white);
        doc.setFont(
          "helvetica",
          "bold"
        );
        doc.setFontSize(6.5);

        doc.text(
          String(number),
          margin + 4.5,
          y + 5.4,
          { align: "center" }
        );

        doc.text(
          title.toUpperCase(),
          margin + 13,
          y + 5.4
        );

        return y + height;
      };

      const card = (
        x,
        y,
        width,
        height,
        fill = light
      ) => {
        doc.setFillColor(...fill);

        doc.roundedRect(
          x,
          y,
          width,
          height,
          1.5,
          1.5,
          "F"
        );
      };

      const imageToDataURL = async (
        imageSource
      ) => {
        if (!imageSource) return null;

        if (
          typeof imageSource === "string" &&
          imageSource.startsWith(
            "data:image/"
          )
        ) {
          return imageSource;
        }

        const imageURL =
          typeof imageSource === "string" &&
          imageSource.startsWith(
            "/uploads/"
          )
            ? `${process.env.REACT_APP_BACKEND_URL}${imageSource}`
            : imageSource;

        const response =
          await fetch(imageURL);

        if (!response.ok) {
          throw new Error(
            `No se pudo cargar la imagen: ${response.status}`
          );
        }

        const blob =
          await response.blob();

        if (
          blob.type === "image/jpeg" ||
          blob.type === "image/jpg" ||
          blob.type === "image/png"
        ) {
          return await new Promise(
            (resolve, reject) => {
              const reader =
                new FileReader();

              reader.onloadend = () =>
                resolve(
                  reader.result
                );

              reader.onerror =
                reject;

              reader.readAsDataURL(
                blob
              );
            }
          );
        }

        return await new Promise(
          (resolve, reject) => {
            const objectURL =
              URL.createObjectURL(
                blob
              );

            const img =
              new Image();

            img.onload = () => {
              try {
                const canvas =
                  document.createElement(
                    "canvas"
                  );

                canvas.width =
                  img.naturalWidth;

                canvas.height =
                  img.naturalHeight;

                const ctx =
                  canvas.getContext(
                    "2d"
                  );

                ctx.drawImage(
                  img,
                  0,
                  0
                );

                URL.revokeObjectURL(
                  objectURL
                );

                resolve(
                  canvas.toDataURL(
                    "image/jpeg",
                    0.9
                  )
                );
              } catch (error) {
      setSavingReport(false);
                URL.revokeObjectURL(
                  objectURL
                );

                reject(error);
              }
            };

            img.onerror = (
              error
            ) => {
              URL.revokeObjectURL(
                objectURL
              );

              reject(error);
            };

            img.src =
              objectURL;
          }
        );
      };

      // =====================================================
      // PAGINA 1 - INFORMACION GENERAL
      // =====================================================

      drawHeader();

      let y = 31;

      // IDENTIFICACION
      y = sectionTitle(
        1,
        "Identificación",
        y
      ) + 1;

      const identificationGap = 4;

      const identificationWidth =
        (contentWidth -
          identificationGap * 2) /
        3;

      const identificationHeight = 22;

      const identificationCards = [
        {
          label: "TRABAJADOR",
          value:
            report.worker_name ||
            "-"
        },
        {
          label: "FECHA",
          value:
            formatDate(report.date)
        },
        {
          label: "ESTADO",
          value: report.reviewed
            ? "REVISADO"
            : "PENDIENTE DE REVISION"
        }
      ];

      identificationCards.forEach(
        (item, index) => {
          const x =
            margin +
            index *
              (identificationWidth +
                identificationGap);

          card(
            x,
            y,
            identificationWidth,
            identificationHeight
          );

          doc.setTextColor(...gray);
          doc.setFont(
            "helvetica",
            "normal"
          );
          doc.setFontSize(6);

          doc.text(
            item.label,
            x + 5,
            y + 7
          );

          doc.setTextColor(
            ...(item.label ===
            "ESTADO"
              ? report.reviewed
                ? green
                : orange
              : dark)
          );

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(
            item.label ===
              "ESTADO"
              ? 7
              : 8.5
          );

          const lines =
            doc.splitTextToSize(
              String(item.value),
              identificationWidth - 10
            );

          doc.text(
            lines,
            x + 5,
            y + 13
          );
        }
      );

      y += 27;

      // RESUMEN
      y = sectionTitle(
        2,
        "Resumen de jornada",
        y
      ) + 1;

      const metricGap = 4;

      const metricWidth =
        (contentWidth -
          metricGap * 3) /
        4;

      const metrics = [
        {
          value:
            formatDuration(
              workdayMinutes
            ),
          label: "Jornada total",
          color: orange
        },
        {
          value:
            formatDuration(
              activityMinutes
            ),
          label: "Tiempo efectivo",
          color: green
        },
        {
          value:
            formatDuration(
              unassignedMinutes
            ),
          label: "No asignado",
          color: gray
        },
        {
          value:
            `${effectiveness}%`,
          label: "Efectividad",
          color: orange
        }
      ];

      metrics.forEach(
        (metric, index) => {
          const x =
            margin +
            index *
              (metricWidth +
                metricGap);

          card(
            x,
            y,
            metricWidth,
            24
          );

          doc.setTextColor(
            ...metric.color
          );

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(10);

          doc.text(
            metric.value,
            x + metricWidth / 2,
            y + 10,
            { align: "center" }
          );

          doc.setTextColor(...gray);

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(5.7);

          doc.text(
            metric.label,
            x + metricWidth / 2,
            y + 17,
            { align: "center" }
          );
        }
      );

      y += 29;

      // JORNADA
      y = sectionTitle(
        3,
        "Control de jornada",
        y
      ) + 1;

      const jornadaGap = 5;

      const jornadaWidth =
        (contentWidth -
          jornadaGap) /
        2;

      card(
        margin,
        y,
        jornadaWidth,
        21
      );

      card(
        margin +
          jornadaWidth +
          jornadaGap,
        y,
        jornadaWidth,
        21
      );

      doc.setTextColor(...gray);
      doc.setFont(
        "helvetica",
        "normal"
      );
      doc.setFontSize(6);

      doc.text(
        "MAÑANA",
        margin + 5,
        y + 7
      );

      doc.text(
        "TARDE",
        margin +
          jornadaWidth +
          jornadaGap +
          5,
        y + 7
      );

      doc.setTextColor(...dark);
      doc.setFont(
        "helvetica",
        "bold"
      );
      doc.setFontSize(9);

      doc.text(
        `${formatTime(
          report.morning_start
        )} - ${formatTime(
          report.morning_end
        )}`,
        margin + 5,
        y + 14
      );

      doc.text(
        `${formatTime(
          report.afternoon_start
        )} - ${formatTime(
          report.afternoon_end
        )}`,
        margin +
          jornadaWidth +
          jornadaGap +
          5,
        y + 14
      );

      y += 26;

      // ACTIVIDADES
      y = sectionTitle(
        4,
        "Actividades realizadas",
        y
      ) + 1;

      const tableX = margin;

      const columns = [
        {
          title: "Nº",
          width: 10
        },
        {
          title: "ACTIVIDAD",
          width: 104
        },
        {
          title: "INICIO",
          width: 22
        },
        {
          title: "FIN",
          width: 22
        },
        {
          title: "DURACIÓN",
          width: 24
        }
      ];

      const tableHeader = 8;

      const drawActivityHeader =
        () => {
          doc.setFillColor(...dark);

          doc.roundedRect(
            tableX,
            y,
            contentWidth,
            tableHeader,
            1,
            1,
            "F"
          );

          let x = tableX;

          columns.forEach(
            (column) => {
              doc.setTextColor(
                ...white
              );

              doc.setFont(
                "helvetica",
                "bold"
              );

              doc.setFontSize(5.7);

              doc.text(
                column.title,
                x +
                  column.width /
                    2,
                y + 5.2,
                {
                  align: "center"
                }
              );

              x += column.width;
            }
          );

          y += tableHeader;
        };

      drawActivityHeader();

      if (activities.length === 0) {
        doc.setFillColor(
          ...light
        );

        doc.rect(
          tableX,
          y,
          contentWidth,
          12,
          "F"
        );

        doc.setTextColor(
          ...gray
        );

        doc.setFont(
          "helvetica",
          "normal"
        );

        doc.setFontSize(6.5);

        doc.text(
          "No se registraron actividades.",
          tableX + 5,
          y + 8
        );

        y += 12;
      } else {
        for (
          let index = 0;
          index < activities.length;
          index++
        ) {
          const activity =
            activities[index];

          const description =
            activity.description ||
            "-";

          const descLines =
            doc.splitTextToSize(
              description,
              columns[1].width - 5
            );

          const rowH =
            Math.max(
              6.2,
              descLines.length *
                3.1 +
                2.5
            );

          // Si la fila no cabe, crear pagina
          // y repetir encabezado
          if (
            y + rowH >
            pageHeight - 18
          ) {
            drawFooter();

            addPage();

            y = 31;

            y = sectionTitle(
              4,
              "Actividades realizadas - continuación",
              y
            ) + 1;

            drawActivityHeader();
          }

          doc.setFillColor(
            ...(index % 2 === 0
              ? [250, 251, 252]
              : [242, 245, 247])
          );

          doc.rect(
            tableX,
            y,
            contentWidth,
            rowH,
            "F"
          );

          doc.setDrawColor(
            ...border
          );

          doc.setLineWidth(0.2);

          doc.rect(
            tableX,
            y,
            contentWidth,
            rowH
          );

          let x = tableX;

          columns.forEach(
            (column, colIndex) => {
              if (colIndex > 0) {
                doc.line(
                  x,
                  y,
                  x,
                  y + rowH
                );
              }

              x += column.width;
            }
          );

          doc.setTextColor(...dark);
          doc.setFont(
            "helvetica",
            "normal"
          );
          doc.setFontSize(5.8);

          doc.text(
            String(index + 1),
            tableX +
              columns[0].width /
                2,
            y +
              rowH / 2 +
              2,
            {
              align: "center"
            }
          );

          doc.text(
            descLines,
            tableX +
              columns[0].width +
              2.5,
            y + 3.8
          );

          const timeBase =
            tableX +
            columns[0].width +
            columns[1].width;

          doc.text(
            formatTime(
              activity.start_time
            ),
            timeBase +
              columns[2].width /
                2,
            y +
              rowH / 2 +
              2,
            {
              align: "center"
            }
          );

          doc.text(
            formatTime(
              activity.end_time
            ),
            timeBase +
              columns[2].width +
              columns[3].width /
                2,
            y +
              rowH / 2 +
              2,
            {
              align: "center"
            }
          );

          doc.text(
            formatDuration(
              Number(
                activity.duration_minutes ||
                  0
              )
            ),
            timeBase +
              columns[2].width +
              columns[3].width +
              columns[4].width /
                2,
            y +
              rowH / 2 +
              2,
            {
              align: "center"
            }
          );

          y += rowH;
        }
      }

      // DESCRIPCION
      if (
        y + 45 >
        pageHeight - 18
      ) {
        drawFooter();
        addPage();
        y = 31;
      }

      y += 5;

      y = sectionTitle(
        5,
        "Descripción del trabajo",
        y
      ) + 1;

      const descriptionLines =
        doc.splitTextToSize(
          report.work_description ||
            "No registrado.",
          contentWidth - 10
        );

      const descriptionHeight =
        Math.min(
          32,
          Math.max(
            20,
            descriptionLines.length *
              3.4 +
              10
          )
        );

      card(
        margin,
        y,
        contentWidth,
        descriptionHeight
      );

      doc.setTextColor(...dark);
      doc.setFont(
        "helvetica",
        "normal"
      );
      doc.setFontSize(6.7);

      doc.text(
        descriptionLines.slice(
          0,
          Math.floor(
            (descriptionHeight - 8) /
              3.4
          )
        ),
        margin + 5,
        y + 9
      );

      y +=
        descriptionHeight + 5;

      // RECURSOS
      if (
        y + 43 >
        pageHeight - 18
      ) {
        drawFooter();
        addPage();
        y = 31;
      }

      const resourceGap = 5;

      const resourceWidth =
        (contentWidth -
          resourceGap) /
        2;

      const resourceHeaderHeight = 8;
      const resourceBodyHeight = 22;

      const resourceY = y;

      const drawResource = (
        x,
        number,
        title,
        value
      ) => {
        doc.setFillColor(...dark);

        doc.roundedRect(
          x,
          resourceY,
          resourceWidth,
          resourceHeaderHeight,
          1.5,
          1.5,
          "F"
        );

        doc.setFillColor(...orange);

        doc.roundedRect(
          x,
          resourceY,
          9,
          resourceHeaderHeight,
          1.5,
          1.5,
          "F"
        );

        doc.setFillColor(...orange);

        doc.rect(
          x + 7,
          resourceY,
          2,
          resourceHeaderHeight,
          "F"
        );

        doc.setTextColor(...white);
        doc.setFont(
          "helvetica",
          "bold"
        );
        doc.setFontSize(6.2);

        doc.text(
          String(number),
          x + 4.5,
          resourceY + 5.4,
          {
            align: "center"
          }
        );

        doc.text(
          title.toUpperCase(),
          x + 13,
          resourceY + 5.4
        );

        const bodyY =
          resourceY +
          resourceHeaderHeight;

        card(
          x,
          bodyY,
          resourceWidth,
          resourceBodyHeight
        );

        doc.setTextColor(...dark);
        doc.setFont(
          "helvetica",
          "normal"
        );
        doc.setFontSize(6.3);

        const lines =
          doc.splitTextToSize(
            value ||
              "No registrado.",
            resourceWidth - 10
          );

        doc.text(
          lines.slice(0, 4),
          x + 5,
          bodyY + 9
        );
      };

      drawResource(
        margin,
        6,
        "Herramientas utilizadas",
        report.tools_used
      );

      drawResource(
        margin +
          resourceWidth +
          resourceGap,
        7,
        "Materiales e insumos",
        report.materials_used
      );

      y =
        resourceY +
        resourceHeaderHeight +
        resourceBodyHeight +
        5;

      // OBSERVACIONES
      if (
        y + 32 >
        pageHeight - 18
      ) {
        drawFooter();
        addPage();
        y = 31;
      }

      y = sectionTitle(
        8,
        "Novedades y observaciones",
        y
      ) + 1;

      const observationLines =
        doc.splitTextToSize(
          report.observations ||
            "No registrado.",
          contentWidth - 10
        );

      const observationHeight =
        Math.min(
          28,
          Math.max(
            20,
            observationLines.length *
              3.4 +
              10
          )
        );

      card(
        margin,
        y,
        contentWidth,
        observationHeight
      );

      doc.setTextColor(...dark);
      doc.setFont(
        "helvetica",
        "normal"
      );
      doc.setFontSize(6.5);

      doc.text(
        observationLines.slice(
          0,
          Math.floor(
            (observationHeight - 8) /
              3.4
          )
        ),
        margin + 5,
        y + 9
      );

      drawFooter();

      // =====================================================
      // EVIDENCIA FOTOGRAFICA
      // =====================================================

      if (images.length > 0) {
        addPage();

        let photoY = 31;

        photoY = sectionTitle(
          9,
          "Evidencia fotográfica",
          photoY
        ) + 4;

        const photoColumns = 2;
        const photoGap = 5;

        const photoWidth =
          (contentWidth -
            photoGap) /
          photoColumns;

        const photoLabelHeight = 7;

        const availableHeight =
          pageHeight -
          photoY -
          24;

        const photoRowsPerPage = 4;

        const photoHeight =
          Math.min(
            52,
            Math.max(
              35,
              (
                availableHeight -
                (photoRowsPerPage - 1) *
                  5
              ) /
                photoRowsPerPage -
                photoLabelHeight
            )
          );

        const rowHeight =
          photoHeight +
          photoLabelHeight +
          5;

        let photoIndex = 0;

        while (
          photoIndex <
          images.length
        ) {
          if (
            photoIndex > 0 &&
            photoIndex % 8 === 0
          ) {
            drawFooter();
            addPage();

            photoY = 31;

            photoY =
              sectionTitle(
                9,
                "Evidencia fotográfica - continuación",
                photoY
              ) + 4;
          }

          const indexInPage =
            photoIndex % 8;

          const column =
            indexInPage % 2;

          const row =
            Math.floor(
              indexInPage / 2
            );

          const photoX =
            margin +
            column *
              (photoWidth +
                photoGap);

          const currentY =
            photoY +
            row * rowHeight;

          card(
            photoX,
            currentY,
            photoWidth,
            photoHeight +
              photoLabelHeight,
            [248, 249, 250]
          );

          try {
            const imageData =
              await imageToDataURL(
                images[photoIndex]
              );

            if (!imageData) {
              throw new Error(
                "Imagen vacía"
              );
            }

            const format =
              imageData.startsWith(
                "data:image/png"
              )
                ? "PNG"
                : "JPEG";

            doc.addImage(
              imageData,
              format,
              photoX + 2,
              currentY + 2,
              photoWidth - 4,
              photoHeight - 4,
              undefined,
              "FAST"
            );
          } catch (
            imageError
          ) {
            console.warn(
              "No se pudo insertar imagen en PDF",
              imageError
            );

            doc.setFillColor(
              238,
              241,
              244
            );

            doc.rect(
              photoX + 2,
              currentY + 2,
              photoWidth - 4,
              photoHeight - 4,
              "F"
            );

            doc.setTextColor(
              ...gray
            );

            doc.setFont(
              "helvetica",
              "normal"
            );

            doc.setFontSize(7);

            doc.text(
              "Imagen no disponible",
              photoX +
                photoWidth / 2,
              currentY +
                photoHeight / 2,
              {
                align: "center"
              }
            );
          }

          doc.setTextColor(...dark);
          doc.setFont(
            "helvetica",
            "bold"
          );
          doc.setFontSize(6);

          doc.text(
            `Foto ${
              photoIndex + 1
            }`,
            photoX +
              photoWidth / 2,
            currentY +
              photoHeight +
              4.5,
            {
              align: "center"
            }
          );

          photoIndex++;
        }

        drawFooter();
      }

      // =====================================================
      // NUMERACION REAL DE PAGINAS
      // =====================================================

      const pageCount =
        doc.getNumberOfPages();

      for (
        let page = 1;
        page <= pageCount;
        page++
      ) {
        doc.setPage(page);

        doc.setTextColor(...dark);
        doc.setFont(
          "helvetica",
          "normal"
        );
        doc.setFontSize(6.5);

        doc.text(
          `Página ${page} de ${pageCount}`,
          pageWidth - margin,
          pageHeight - 10,
          {
            align: "right"
          }
        );
      }

      const pdfBlob =
        doc.output("blob");

      const pdfURL =
        URL.createObjectURL(
          pdfBlob
        );

      window.open(
        pdfURL,
        "_blank"
      );

      setTimeout(() => {
        URL.revokeObjectURL(
          pdfURL
        );
      }, 60000);
    } catch (error) {
      setSavingReport(false);
      console.error(
        "Error exportando reporte de trabajo:",
        error
      );

      alert(
        "No se pudo generar el PDF. Revise la consola para más detalles."
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingReport) return;
    setSavingReport(true);

    try {
      const validActivities = activities
        .filter(
          (activity) =>
            activity.description.trim() &&
            activity.start_time &&
            activity.end_time
        )
        .map((activity) => ({
          description: activity.description.trim(),
          start_time: activity.start_time,
          end_time: activity.end_time,
          duration_minutes: calculateActivityDuration(
            activity.start_time,
            activity.end_time
          )
        }));

      const workDescription = validActivities
        .map((activity) => activity.description)
        .join("\\n");

      const reportPayload = {
        date: formData.date,
        work_description: workDescription,

        morning_start: workday.morning_start || null,
        morning_end: workday.morning_end || null,
        afternoon_start: workday.afternoon_start || null,
        afternoon_end: workday.afternoon_end || null,

        tools_used: formData.tools_used,
        materials_used: formData.materials_used,
        observations: formData.observations,

        activities: validActivities
      };

      let response;

      if (editingReport) {
        response = await axios.put(
          `${API}/work-reports/${editingReport.id}`,
          reportPayload
        );
      } else {
        response = await axios.post(
          `${API}/work-reports`,
          reportPayload
        );
      }

      if (uploadedImages.length > 0) {
        for (const imageFile of uploadedImages) {
          const formDataImg = new FormData();
          formDataImg.append('file', imageFile);

          await axios.post(
            `${API}/work-reports/${response.data.id}/images`,
            formDataImg
          );
        }
      }

      toast.success(
        editingReport
          ? 'Reporte actualizado exitosamente'
          : 'Reporte creado exitosamente'
      );

      setDialogOpen(false);
      resetForm();
      setEditingReport(null);
      fetchReports();

    } catch (error) {
      setSavingReport(false);
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
        'Error al guardar'
      );
    }
  };


  const editWorkReport = (report) => {
    setEditingReport(report);

    setFormData({
      date: report.date || new Date().toISOString().split('T')[0],
      work_description: report.work_description || '',
      tools_used: report.tools_used || '',
      materials_used: report.materials_used || '',
      observations: report.observations || ''
    });

    setActivities(
      Array.isArray(report.activities) && report.activities.length > 0
        ? report.activities.map((activity, index) => ({
            id: activity.id || `${report.id}-${index}`,
            description: activity.description || '',
            start_time: activity.start_time || '',
            end_time: activity.end_time || '',
            duration_minutes:
              activity.duration_minutes ||
              calculateActivityDuration(
                activity.start_time || '',
                activity.end_time || ''
              )
          }))
        : []
    );

    setWorkday({
      morning_start: report.morning_start || '',
      morning_end: report.morning_end || '',
      afternoon_start: report.afternoon_start || '',
      afternoon_end: report.afternoon_end || ''
    });

    setUploadedImages([]);
    setDialogOpen(true);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setUploadedImages([...uploadedImages, ...files]);
  };

  const removeImage = (index) => {
    setUploadedImages(uploadedImages.filter((_, i) => i !== index));
  };
  const openReviewDialog = (reportId) => {
    setReviewData({
        reportId,
        comment: ""
    });
    setReviewDialogOpen(true);
};

  const reviewReport = async (reportId) => {
  try {
    await axios.put(`${API}/work-reports/${reportId}/review`);

    toast.success("Reporte marcado como revisado");

    fetchReports();
  } catch (error) {
      setSavingReport(false);
    toast.error(
      error.response?.data?.detail ||
      "No se pudo marcar el reporte"
    );
  }
};
  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      work_description: '',
      tools_used: '',
      materials_used: '',
      observations: ''
    });
    setUploadedImages([]);
  };
  const showPreviousImage = () => {
  if (currentImageIndex > 0) {
    const newIndex = currentImageIndex - 1;
    setCurrentImageIndex(newIndex);
    setSelectedImage(selectedImages[newIndex]);
  }
};

const showNextImage = () => {
  if (currentImageIndex < selectedImages.length - 1) {
    const newIndex = currentImageIndex + 1;
    setCurrentImageIndex(newIndex);
    setSelectedImage(selectedImages[newIndex]);
  }
};

const downloadReportImages = async (images, reportId = "trabajo") => {
  if (!Array.isArray(images) || images.length === 0) {
    toast.error("Este reporte no tiene fotografías para descargar");
    return;
  }

  for (let index = 0; index < images.length; index++) {
    const item = images[index];
    const imageUrl =
      typeof item === "string" && item.startsWith("/uploads/")
        ? `${window.location.origin}${item}`
        : item;

    if (!imageUrl) continue;

    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error("No se pudo descargar la fotografía");

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const extension = blob.type.includes("png")
        ? "png"
        : blob.type.includes("webp")
          ? "webp"
          : blob.type.includes("jpeg") || blob.type.includes("jpg")
            ? "jpg"
            : "img";

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `reporte-${reportId}-foto-${index + 1}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => URL.revokeObjectURL(objectUrl), 3000);
      await new Promise(resolve => setTimeout(resolve, 350));
    } catch (error) {
      console.error("Error descargando fotografía:", imageUrl, error);
      window.open(imageUrl, "_blank", "noopener,noreferrer");
    }
  }

  toast.success("Se procesaron las descargas de fotografías");
};
  const handleDeleteReport = async (reportId) => {
  const confirmDelete = window.confirm(
    "¿Está seguro de eliminar este reporte? Esta acción no se puede deshacer."
  );

  if (!confirmDelete) return;

  try {
    await axios.delete(`${API}/work-reports/${reportId}`);

    fetchReports();

    alert("Reporte eliminado correctamente.");
  } catch (error) {
      setSavingReport(false);
    console.error(error);
    alert(
      error?.response?.data?.detail ||
      "No se pudo eliminar el reporte."
    );
  }
};
 
  async function confirmReview() {
    try {
      await axios.put(
        `${API}/work-reports/${reviewData.reportId}/review`,
        {
          comment: reviewData.comment
        }
      );

      toast.success("Reporte revisado correctamente");

      setReviewDialogOpen(false);

      setReviewData({
        reportId: "",
        comment: ""
      });

      fetchReports();

    } catch (error) {
      setSavingReport(false);
      toast.error(
        error.response?.data?.detail ||
        "No se pudo revisar el reporte"
      );
    }
  }
  const groupedReports = filteredReports.reduce((acc, report) => {
  const date = report.date;

  if (!acc[date]) {
    acc[date] = [];
  }

  acc[date].push(report);

  return acc;
}, {});

const visibleReports = [...reports].sort((a, b) => {
  const dateA = a?.date || "";
  const dateB = b?.date || "";
  return dateB.localeCompare(dateA);
});

const toggleDate = (date) => {
  setExpandedDate((prev) => ({
    ...prev,
    [date]: !prev[date],
  }));
};
const reportsOfSelectedDay = reports.filter(report => {
  if (!report.date) return false;

  return report.date.substring(0, 10) === selectedDate;
});

const totalReports = reportsOfSelectedDay.length;

const reviewedReports = reportsOfSelectedDay.filter(
  report => report.reviewed
).length;

const pendingReports = totalReports - reviewedReports;

const totalImages = reportsOfSelectedDay.reduce(
  (total, report) => total + (report.images?.length || 0),
  0
);
const parseLocalDate = (dateString) => {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
};
const totalWorkers = new Set(
  reportsOfSelectedDay.map(report => report.user_id)
).size;
  return (
    <div data-testid="work-reports-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Reportes de Trabajo
          </h1>
          <p className="text-slate-600 mt-2">Registro diario de actividades</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-report-button" className="btn-primary">
              <Plus size={20} className="mr-2" />
              Nuevo Reporte
            </Button>
          </DialogTrigger>
          <DialogContent className="w-[95vw] max-w-[1200px] max-h-[90vh] overflow-y-auto overflow-x-hidden">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                {editingReport ? 'Editar Reporte de Trabajo' : 'Nuevo Reporte de Trabajo'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} data-testid="work-report-form" className="space-y-5">

              {/* INFORMACIÓN DEL REPORTE */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Calendar size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">
                      Información del reporte
                    </h3>
                    <p className="text-sm text-slate-500">
                      Indique la fecha correspondiente a la jornada
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Fecha del trabajo
                  </label>
                  <input
                    type="date"
                    data-testid="date-input"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="input-field"
                    required
                  />
                </div>
              </div>

              {/* CONTROL DE JORNADA */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Calendar size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">
                      Control de jornada
                    </h3>
                    <p className="text-sm text-slate-500">
                      Registre los horarios de inicio y finalización de cada turno
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                  {/* TURNO MAÑANA */}
                  <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                    <h4 className="font-bold text-slate-800 mb-4">
                      Turno mañana
                    </h4>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Inicio
                        </label>
                        <input
                          type="time"
                          value={workday.morning_start}
                          onChange={(e) =>
                            updateWorkday('morning_start', e.target.value)
                          }
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Fin
                        </label>
                        <input
                          type="time"
                          value={workday.morning_end}
                          onChange={(e) =>
                            updateWorkday('morning_end', e.target.value)
                          }
                          className="input-field"
                        />
                      </div>
                    </div>

                    <div className="mt-4 rounded-lg bg-white border border-blue-100 px-4 py-3 flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-600">
                        Duración
                      </span>
                      <span className="font-bold text-blue-700">
                        {formatDuration(getMorningMinutes())}
                      </span>
                    </div>
                  </div>

                  {/* TURNO TARDE */}
                  <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4">
                    <h4 className="font-bold text-slate-800 mb-4">
                      Turno tarde
                    </h4>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Inicio
                        </label>
                        <input
                          type="time"
                          value={workday.afternoon_start}
                          onChange={(e) =>
                            updateWorkday('afternoon_start', e.target.value)
                          }
                          className="input-field"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Fin
                        </label>
                        <input
                          type="time"
                          value={workday.afternoon_end}
                          onChange={(e) =>
                            updateWorkday('afternoon_end', e.target.value)
                          }
                          className="input-field"
                        />
                      </div>
                    </div>

                    <div className="mt-4 rounded-lg bg-white border border-indigo-100 px-4 py-3 flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-600">
                        Duración
                      </span>
                      <span className="font-bold text-indigo-700">
                        {formatDuration(getAfternoonMinutes())}
                      </span>
                    </div>
                  </div>

                </div>

                {/* TOTAL JORNADA */}
                <div className="mt-5 rounded-xl bg-slate-800 px-5 py-4 flex items-center justify-between">
                  <span className="font-semibold text-white">
                    Total jornada
                  </span>
                  <span className="text-xl font-bold text-white">
                    {formatDuration(getWorkdayMinutes())}
                  </span>
                </div>
              </div>

              {/* ACTIVIDADES */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <ClipboardList size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-800">
                        Actividades realizadas
                      </h3>
                      <p className="text-sm text-slate-500">
                        Agregue cada actividad con su horario de inicio y finalización
                      </p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={addActivity}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus size={18} className="mr-2" />
                    Agregar actividad
                  </Button>
                </div>

                {activities.length === 0 ? (
                  <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 text-center">
                    <ClipboardList size={34} className="mx-auto text-slate-300 mb-3" />
                    <p className="font-semibold text-slate-600">
                      No hay actividades agregadas
                    </p>
                    <p className="text-sm text-slate-400 mt-1">
                      Presione “Agregar actividad” para registrar la primera labor.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activities.map((activity, index) => (
                      <div
                        key={activity.id}
                        className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <span className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-bold">
                              {index + 1}
                            </span>
                            <span className="font-semibold text-slate-700">
                              Actividad {index + 1}
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeActivity(activity.id)}
                            className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition"
                            title="Eliminar actividad"
                          >
                            <X size={18} />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-[minmax(300px,2fr)_145px_145px_160px] gap-4 items-end">
                          <div className="min-w-0">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              Descripción de la actividad
                            </label>
                            <input
                              type="text"
                              value={activity.description}
                              onChange={(e) =>
                                updateActivity(
                                  activity.id,
                                  'description',
                                  e.target.value
                                )
                              }
                              className="input-field"
                              placeholder="Ej.: Limpieza del área de piscina"
                            />
                          </div>

                          <div className="min-w-0">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              Inicio
                            </label>
                            <input
                              type="time"
                              value={activity.start_time}
                              onChange={(e) =>
                                updateActivity(
                                  activity.id,
                                  'start_time',
                                  e.target.value
                                )
                              }
                              className="input-field w-full min-w-0 px-3"
                            />
                          </div>

                          <div className="min-w-0">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              Fin
                            </label>
                            <input
                              type="time"
                              value={activity.end_time}
                              onChange={(e) =>
                                updateActivity(
                                  activity.id,
                                  'end_time',
                                  e.target.value
                                )
                              }
                              className="input-field w-full min-w-0 px-3"
                            />
                          </div>

                          <div className="min-w-0">
                            <label className="block text-sm font-semibold text-slate-700 mb-2">
                              Duración
                            </label>
                            <div className="h-10 px-3 rounded-md border border-slate-200 bg-white flex items-center justify-center font-semibold text-emerald-700">
                              {formatDuration(activity.duration_minutes)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="flex items-center justify-between rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
                      <span className="font-semibold text-slate-700">
                        Tiempo total de actividades
                      </span>
                      <span className="text-lg font-bold text-emerald-700">
                        {formatDuration(getTotalActivityMinutes())}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* RECURSOS */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <span className="text-lg">🧰</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">
                      Recursos utilizados
                    </h3>
                    <p className="text-sm text-slate-500">
                      Registre las herramientas e insumos utilizados
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Herramientas utilizadas
                    </label>
                    <textarea
                      data-testid="tools-input"
                      value={formData.tools_used}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          tools_used: e.target.value
                        })
                      }
                      className="input-field min-h-[110px]"
                      rows={4}
                      placeholder="Ej.: Machete, rastrillo, podadora, escoba, taladro..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Insumos / materiales utilizados
                    </label>
                    <textarea
                      data-testid="materials-input"
                      value={formData.materials_used}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          materials_used: e.target.value
                        })
                      }
                      className="input-field min-h-[110px]"
                      rows={4}
                      placeholder="Ej.: Bolsas de basura, pintura, tornillos, fertilizante..."
                    />
                  </div>

                </div>
              </div>

              {/* OBSERVACIONES */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <span className="text-lg">⚠️</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">
                      Novedades y observaciones
                    </h3>
                    <p className="text-sm text-slate-500">
                      Informe problemas, pendientes o recomendaciones
                    </p>
                  </div>
                </div>

                <textarea
                  data-testid="observations-input"
                  value={formData.observations}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      observations: e.target.value
                    })
                  }
                  className="input-field min-h-[110px]"
                  rows={4}
                  placeholder="Ej.: Se encontró una llave de agua dañada. Queda pendiente su reparación..."
                />
              </div>

              {/* EVIDENCIA */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Upload size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800">
                      Evidencia fotográfica
                    </h3>
                    <p className="text-sm text-slate-500">
                      Adjunte fotografías que respalden el trabajo realizado
                    </p>
                  </div>
                </div>

                <label className="flex flex-col items-center justify-center w-full min-h-[120px] border-2 border-dashed border-slate-300 rounded-xl cursor-pointer bg-slate-50 hover:bg-slate-100 transition">
                  <Upload size={28} className="text-slate-400 mb-2" />
                  <span className="text-sm font-semibold text-slate-600">
                    Seleccionar fotografías
                  </span>
                  <span className="text-xs text-slate-400 mt-1">
                    Puede seleccionar varias imágenes
                  </span>

                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>

                {uploadedImages.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-slate-700">
                        Fotografías seleccionadas
                      </span>
                      <span className="text-xs text-slate-500">
                        {uploadedImages.length} archivo(s)
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {uploadedImages.map((img, index) => (
                        <div
                          key={index}
                          className="relative group rounded-xl overflow-hidden border border-slate-200 bg-slate-100"
                        >
                          <img
                            src={typeof img === 'string' ? img : URL.createObjectURL(img)}
                            alt={`Evidencia ${index + 1}`}
                            className="w-full h-28 object-cover"
                          />

                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1">
                            <span className="text-xs text-white">
                              Foto {index + 1}
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 shadow hover:bg-red-600 transition"
                            title="Eliminar fotografía"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* ACCIONES */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
                <Button
                  type="button"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </Button>

                <Button
                  type="submit"
                  className="btn-primary"
                  disabled={savingReport}
                >
                  {savingReport ? '⏳ Guardando reporte...' : 'Guardar Reporte'}
                </Button>
              </div>

            </form>
          </DialogContent>
        </Dialog>
      </div>
      {user?.permissions?.work_reports?.includes('approve') && (
      <div className="hidden">

  <div className="flex items-center justify-between">

    <div>
      <h2 className="text-2xl font-bold text-slate-800">
        📊 Panel Diario de Reportes
      </h2>

      <p className="text-slate-500 text-sm">
        Resumen del día seleccionado
      </p>
    </div>

    <input
      type="date"
      value={selectedDate}
      onChange={(e) => setSelectedDate(e.target.value)}
      className="border rounded-lg px-4 py-2"
    />

  </div>
  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">

  <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
    <p className="text-sm text-blue-600 font-medium">
      📝 Reportes
    </p>

    <h2 className="text-3xl font-bold text-blue-700 mt-2">
      {totalReports}
    </h2>
  </div>

  <div className="bg-green-50 rounded-xl p-5 border border-green-100">
    <p className="text-sm text-green-600 font-medium">
      ✅ Revisados
    </p>

    <h2 className="text-3xl font-bold text-green-700 mt-2">
      {reviewedReports}
    </h2>
  </div>

  <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-100">
    <p className="text-sm text-yellow-700 font-medium">
      ⏳ Pendientes
    </p>

    <h2 className="text-3xl font-bold text-yellow-700 mt-2">
      {pendingReports}
    </h2>
  </div>

  <div className="bg-purple-50 rounded-xl p-5 border border-purple-100">
    <p className="text-sm text-purple-700 font-medium">
      📷 Evidencias
    </p>

    <h2 className="text-3xl font-bold text-purple-700 mt-2">
      {totalImages}
    </h2>
  </div>

  <div className="bg-orange-50 rounded-xl p-5 border border-orange-100">
    <p className="text-sm text-orange-700 font-medium">
      👷 Personal
    </p>

    <h2 className="text-3xl font-bold text-orange-700 mt-2">
      {totalWorkers}
    </h2>
  </div>

</div>

</div>

  )}

  <div className="bg-white rounded-xl border shadow-sm p-4 mb-5">
    <div className="flex items-center gap-2 mb-4">
      <Filter className="w-5 h-5 text-slate-500" />
      <h3 className="font-semibold text-slate-800">Filtros de reportes</h3>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
      <div className="relative lg:col-span-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar trabajador o actividad..."
          className="w-full border rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
        />
      </div>

      <select
        value={workerFilter}
        onChange={(e) => setWorkerFilter(e.target.value)}
        className="border rounded-lg px-3 py-2 text-sm bg-white"
      >
        <option value="">Todos los trabajadores</option>
        {filteredWorkers.map((worker) => (
          <option key={worker.id} value={worker.id}>
            {worker.name}
          </option>
        ))}
      </select>

      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="border rounded-lg px-3 py-2 text-sm bg-white"
      >
        <option value="all">Todos los estados</option>
        <option value="pending">Pendientes</option>
        <option value="reviewed">Revisados</option>
      </select>

      <button
        type="button"
        onClick={() => {
          setSearchTerm('');
          setWorkerFilter('');
          setStatusFilter('all');
          setDateFromFilter('');
          setDateToFilter('');
        }}
        className="border rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
      >
        Limpiar filtros
      </button>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 max-w-xl">
      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Desde
        </label>
        <input
          type="date"
          value={dateFromFilter}
          onChange={(e) => setDateFromFilter(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">
          Hasta
        </label>
        <input
          type="date"
          value={dateToFilter}
          onChange={(e) => setDateToFilter(e.target.value)}
          className="w-full border rounded-lg px-3 py-2 text-sm"
        />
      </div>
    </div>

    <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
      Mostrando <span className="font-semibold text-slate-700">{filteredReports.length}</span>
      {' '}de <span className="font-semibold text-slate-700">{reports.length}</span> reportes
    </div>
  </div>

  {loading ? (
        <div>Cargando...</div>
      ) : (
        <div className="space-y-3">
          {Object.entries(groupedReports).map(([date, reportsOfDay]) => (
            <div key={date} className="mb-2 xl:col-span-2">
              <div
                onClick={() => toggleDate(date)}
                className="flex items-center justify-between gap-3 bg-slate-100 hover:bg-slate-200 rounded-lg px-3 py-2 cursor-pointer transition-colors border border-slate-200"
              >
                <div className="font-bold text-sm sm:text-base text-slate-700 capitalize">
                  {parseLocalDate(date).toLocaleDateString('es-BO', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>

                <div className="flex items-center flex-wrap gap-4">
                  <span className="text-sm text-slate-500">
                    {reportsOfDay.length} reporte{reportsOfDay.length !== 1 ? 's' : ''}
                  </span>

                  <span className="text-2xl font-bold">{expandedDate[date] ? '−' : '+'}</span>
                </div>
              </div>

              {expandedDate[date] && (
                <div className="space-y-2">
                  {reportsOfDay.map((report) => (
                    <div key={report.id}>
                      <div
                        className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden"
                        onClick={() =>
                          setExpandedReport(expandedReport === report.id ? null : report.id)
                        }
                      >
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-x-5 gap-y-2 items-center px-4 py-3">

                          <div className="">
                            <div className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Fecha</div>
                            <div className="font-semibold text-slate-800 mt-1">
                              {report.date.split("-").reverse().join("/")}
                            </div>
                          </div>

                          <div className="">
                            <div className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Trabajador</div>
                            <div className="font-semibold text-slate-800 mt-1 truncate text-sm">
                              {report.worker_name || "Sin asignar"}
                            </div>
                          </div>

                          <div className="">
                            <div className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Actividades</div>
                            <div className="flex items-center flex-wrap gap-2 mt-1">
                              <ClipboardList className="w-4 h-4 text-orange-500" />
                              <span className="font-semibold text-slate-800">
                                {report.activities?.length || 0}
                              </span>
                            </div>
                          </div>

                          <div className="">
                            <div className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Jornada</div>
                            <div className="flex items-center flex-wrap gap-2 mt-1">
                              <Clock className="w-4 h-4 text-blue-500" />
                              <span className="font-semibold text-slate-800">
                                {formatDuration(
  calculateWorkdayRange(report.morning_start, report.morning_end) +
  calculateWorkdayRange(report.afternoon_start, report.afternoon_end)
)}
                              </span>
                            </div>
                          </div>

                          <div className="">
                            <div className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Efectivo</div>
                            <div className="font-semibold text-green-600 mt-1">
                              {formatDuration(
  report.activities?.reduce(
    (total, activity) => total + Number(activity.duration_minutes || 0),
    0
  ) || 0
)}
                            </div>
                          </div>

                          <div className="">
                            <div className="text-[10px] text-slate-400 uppercase font-semibold tracking-wide">Fotos</div>
                            <div className="flex items-center flex-wrap gap-1 mt-1">
                              <Image className="w-4 h-4 text-purple-500" />
                              <span className="font-semibold text-slate-800">
                                {report.images?.length || 0}
                              </span>
                            </div>
                          </div>

                          <div className=" flex items-center flex-wrap justify-between gap-2">
                            <div>
                              {report.reviewed ? (
                                <span className="inline-flex items-center flex-wrap gap-1 bg-green-100 text-green-700 px-2.5 py-1.5 rounded-lg text-xs font-semibold">
                                  <CheckCircle2 className="w-4 h-4" />
                                  Revisado
                                </span>
                              ) : (
                                <span className="inline-flex items-center flex-wrap gap-1 bg-yellow-100 text-yellow-700 px-2.5 py-1.5 rounded-lg text-xs font-semibold">
                                  <CircleAlert className="w-4 h-4" />
                                  Pendiente
                                </span>
                              )}
                            </div>

                            <div className="flex items-center flex-wrap gap-1">
                              {user?.permissions?.work_reports?.includes('approve') && !report.reviewed && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openReviewDialog(report.id);
                                  }}
                                  className="p-1.5 rounded-md text-green-600 hover:bg-green-50"
                                  title="Revisar reporte"
                                >
                                  <ClipboardCheck className="w-4 h-4" />
                                </button>
                              )}

                              {user?.permissions?.work_reports?.includes('delete') && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteReport(report.id);
                                  }}
                                  className="p-1.5 rounded-md text-red-600 hover:bg-red-50"
                                  title="Eliminar reporte"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  editWorkReport(report);
                                }}
                                className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50"
                                title="Editar reporte"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  exportWorkReport(report);
                                }}
                                className="p-1.5 rounded-md text-slate-700 hover:bg-slate-100"
                                title="Exportar reporte en PDF"
                              >
                                <FileText className="w-4 h-4" />
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setViewingReport(report);
                                }}
                                className="p-1.5 rounded-md text-slate-600 hover:bg-slate-100"
                                title="Ver reporte"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </div>

                        </div>

                        <div className="px-4 py-2 text-xs sm:text-sm text-slate-500 border-t border-slate-100 bg-slate-50/50 truncate">
                          <span className="font-medium text-slate-700">Resumen:</span>{" "}
                          {report.work_description?.substring(0, 120) || "Sin descripción"}
                          {report.work_description?.length > 120 ? "..." : ""}
                        </div>
                      </div>

                      {expandedReport === report.id && (
                        <div className="p-5 bg-slate-50 border-t border-slate-200 space-y-5">

                          {/* CONTROL DE JORNADA */}
                          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="flex items-center flex-wrap gap-3 mb-4">
                              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center flex-wrap justify-center">
                                <Calendar size={20} className="text-blue-600" />
                              </div>
                              <div>
                                <h3 className="text-lg font-bold text-slate-800">
                                  Control de jornada
                                </h3>
                                <p className="text-sm text-slate-500">
                                  Horario registrado de la jornada laboral
                                </p>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                              <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4">
                                <div className="flex items-center flex-wrap justify-between mb-3">
                                  <span className="font-bold text-slate-700">
                                    Turno mañana
                                  </span>
                                  <span className="font-bold text-blue-700">
                                    {formatDuration(report.morning_minutes || calculateWorkdayRange(report.morning_start, report.morning_end))}
                                  </span>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                                    <p className="text-xs text-slate-500">Inicio</p>
                                    <p className="font-bold text-slate-800">
                                      {report.morning_start || "—"}
                                    </p>
                                  </div>

                                  <div className="bg-white rounded-lg p-3 border border-blue-100">
                                    <p className="text-xs text-slate-500">Fin</p>
                                    <p className="font-bold text-slate-800">
                                      {report.morning_end || "—"}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4">
                                <div className="flex items-center flex-wrap justify-between mb-3">
                                  <span className="font-bold text-slate-700">
                                    Turno tarde
                                  </span>
                                  <span className="font-bold text-indigo-700">
                                    {formatDuration(report.afternoon_minutes || calculateWorkdayRange(report.afternoon_start, report.afternoon_end))}
                                  </span>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                  <div className="bg-white rounded-lg p-3 border border-indigo-100">
                                    <p className="text-xs text-slate-500">Inicio</p>
                                    <p className="font-bold text-slate-800">
                                      {report.afternoon_start || "—"}
                                    </p>
                                  </div>

                                  <div className="bg-white rounded-lg p-3 border border-indigo-100">
                                    <p className="text-xs text-slate-500">Fin</p>
                                    <p className="font-bold text-slate-800">
                                      {report.afternoon_end || "—"}
                                    </p>
                                  </div>
                                </div>
                              </div>

                            </div>

                            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">

                              <div className="rounded-xl bg-slate-800 p-4">
                                <p className="text-sm text-slate-300">
                                  Total jornada
                                </p>
                                <p className="text-2xl font-bold text-white mt-1">
                                  {formatDuration(calculateWorkdayRange(report.morning_start, report.morning_end) + calculateWorkdayRange(report.afternoon_start, report.afternoon_end))}
                                </p>
                              </div>

                              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                                <p className="text-sm text-emerald-700">
                                  Tiempo efectivo
                                </p>
                                <p className="text-2xl font-bold text-emerald-700 mt-1">
                                  {formatDuration(report.activities?.reduce((total, activity) => total + (activity.duration_minutes || 0), 0) || 0)}
                                </p>
                              </div>

                              <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
                                <p className="text-sm text-amber-700">
                                  Tiempo no asignado
                                </p>
                                <p className="text-2xl font-bold text-amber-700 mt-1">
                                  {formatDuration(Math.max(0, (calculateWorkdayRange(report.morning_start, report.morning_end) + calculateWorkdayRange(report.afternoon_start, report.afternoon_end)) - (report.activities?.reduce((total, activity) => total + (activity.duration_minutes || 0), 0) || 0)))}
                                </p>
                              </div>

                            </div>
                          </div>

                          {/* ACTIVIDADES */}
                          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="flex items-center flex-wrap gap-3 mb-4">
                              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center flex-wrap justify-center">
                                <ClipboardList size={20} className="text-emerald-600" />
                              </div>
                              <div>
                                <h3 className="text-lg font-bold text-slate-800">
                                  Actividades realizadas
                                </h3>
                                <p className="text-sm text-slate-500">
                                  Detalle de las labores realizadas durante la jornada
                                </p>
                              </div>
                            </div>

                            {report.activities?.length > 0 ? (
                              <div className="space-y-3">

                                {report.activities.map((activity, index) => (
                                  <div
                                    key={index}
                                    className="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                  >
                                    <div className="flex items-start gap-3">

                                      <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center flex-wrap justify-center font-bold text-sm shrink-0">
                                        {index + 1}
                                      </div>

                                      <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-slate-800">
                                          {activity.description}
                                        </p>

                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">

                                          <div className="bg-white rounded-lg border border-slate-200 p-3">
                                            <p className="text-xs text-slate-500">
                                              Inicio
                                            </p>
                                            <p className="font-bold text-slate-800">
                                              {activity.start_time || "—"}
                                            </p>
                                          </div>

                                          <div className="bg-white rounded-lg border border-slate-200 p-3">
                                            <p className="text-xs text-slate-500">
                                              Fin
                                            </p>
                                            <p className="font-bold text-slate-800">
                                              {activity.end_time || "—"}
                                            </p>
                                          </div>

                                          <div className="bg-emerald-50 rounded-lg border border-emerald-100 p-3">
                                            <p className="text-xs text-emerald-700">
                                              Duración
                                            </p>
                                            <p className="font-bold text-emerald-700">
                                              {formatDuration(activity.duration_minutes || 0)}
                                            </p>
                                          </div>

                                        </div>
                                      </div>

                                    </div>
                                  </div>
                                ))}

                                <div className="flex items-center flex-wrap justify-between rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
                                  <span className="font-semibold text-slate-700">
                                    Tiempo total de actividades
                                  </span>
                                  <span className="text-lg font-bold text-emerald-700">
                                    {formatDuration(
                                      report.effective_minutes ||
                                      report.activities.reduce(
                                        (total, activity) =>
                                          total + (activity.duration_minutes || 0),
                                        0
                                      )
                                    )}
                                  </span>
                                </div>

                              </div>
                            ) : (
                              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                                <p className="text-sm font-semibold text-slate-700 mb-2">
                                  Actividades registradas
                                </p>
                                <p className="bg-white rounded-lg border border-slate-200 p-4 whitespace-pre-wrap">
                                  {report.work_description || "No se registraron actividades."}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* RECURSOS */}
                          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="flex items-center flex-wrap gap-3 mb-4">
                              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center flex-wrap justify-center">
                                <span className="text-lg">🧰</span>
                              </div>
                              <div>
                                <h3 className="text-lg font-bold text-slate-800">
                                  Recursos utilizados
                                </h3>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                                <p className="text-sm font-semibold text-slate-600 mb-2">
                                  Herramientas
                                </p>
                                <p className="whitespace-pre-wrap text-slate-800">
                                  {report.tools_used || "No se registraron herramientas."}
                                </p>
                              </div>

                              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                                <p className="text-sm font-semibold text-slate-600 mb-2">
                                  Materiales / insumos
                                </p>
                                <p className="whitespace-pre-wrap text-slate-800">
                                  {report.materials_used || "No se registraron materiales."}
                                </p>
                              </div>

                            </div>
                          </div>

                          {/* OBSERVACIONES */}
                          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="flex items-center flex-wrap gap-3 mb-3">
                              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center flex-wrap justify-center">
                                <span className="text-lg">⚠️</span>
                              </div>
                              <h3 className="text-lg font-bold text-slate-800">
                                Novedades y observaciones
                              </h3>
                            </div>

                            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                              <p className="whitespace-pre-wrap text-slate-800">
                                {report.observations || "No se registraron observaciones."}
                              </p>
                            </div>
                          </div>

                          {/* SUPERVISOR */}
                          {report.supervisor_comment && (
                            <div className="rounded-2xl border border-green-200 bg-green-50 p-5">
                              <p className="text-sm font-semibold text-green-700 mb-2">
                                Observación del supervisor
                              </p>
                              <p className="whitespace-pre-wrap text-green-900">
                                {report.supervisor_comment}
                              </p>
                            </div>
                          )}

                          {/* FOTOGRAFÍAS */}
                          {report.images?.length > 0 && (
                            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                              <div className="flex items-center flex-wrap justify-between mb-4">
                                <div>
                                  <h3 className="text-lg font-bold text-slate-800">
                                    Evidencia fotográfica
                                  </h3>
                                  <p className="text-sm text-slate-500">
                                    {report.images.length} fotografía{report.images.length !== 1 ? "s" : ""}
                                  </p>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {report.images.map((img, index) => (
                                  <img
                                    key={index}
                                    src={img.startsWith("/uploads/") ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                                    alt={`Trabajo ${index + 1}`}
                                    className="rounded-xl border border-slate-200 object-cover h-40 w-full cursor-pointer hover:opacity-90 transition"
                                    onClick={() => {
                                      setSelectedImages(report.images);
                                      setCurrentImageIndex(index);
                                      setSelectedImage(img);
                                    }}
                                  />
                                ))}
                              </div>
                            </div>
                          )}

                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {viewingReport && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setViewingReport(null)}
          />

          <div className="relative ml-auto h-full w-full max-w-2xl bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h2 className="text-xl font-bold text-slate-800">
                  Reporte de Trabajo
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {viewingReport.worker_name || "Sin trabajador"} ·{" "}
                  {viewingReport.date
                    ? viewingReport.date.split("-").reverse().join("/")
                    : ""}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setViewingReport(null)}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
                title="Cerrar"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-xl bg-slate-800 p-4">
                  <p className="text-sm text-slate-300">Jornada</p>
                  <p className="text-xl font-bold text-white mt-1">
                    {formatDuration(
                      calculateWorkdayRange(
                        viewingReport.morning_start,
                        viewingReport.morning_end
                      ) +
                      calculateWorkdayRange(
                        viewingReport.afternoon_start,
                        viewingReport.afternoon_end
                      )
                    )}
                  </p>
                </div>

                <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
                  <p className="text-sm text-emerald-700">Efectivo</p>
                  <p className="text-xl font-bold text-emerald-700 mt-1">
                    {formatDuration(
                      viewingReport.activities?.reduce(
                        (total, activity) =>
                          total + Number(activity.duration_minutes || 0),
                        0
                      ) || 0
                    )}
                  </p>
                </div>

                <div className="rounded-xl bg-orange-50 border border-orange-100 p-4">
                  <p className="text-sm text-orange-700">Actividades</p>
                  <p className="text-xl font-bold text-orange-700 mt-1">
                    {viewingReport.activities?.length || 0}
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-800 mb-3">
                  Control de jornada
                </h3>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500">Mañana</span>
                    <p className="font-semibold">
                      {viewingReport.morning_start || "--:--"} →{" "}
                      {viewingReport.morning_end || "--:--"}
                    </p>
                  </div>

                  <div>
                    <span className="text-slate-500">Tarde</span>
                    <p className="font-semibold">
                      {viewingReport.afternoon_start || "--:--"} →{" "}
                      {viewingReport.afternoon_end || "--:--"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-800 mb-3">
                  Actividades
                </h3>

                <div className="space-y-3">
                  {viewingReport.activities?.length ? (
                    viewingReport.activities.map((activity, index) => (
                      <div
                        key={index}
                        className="rounded-xl bg-slate-50 border border-slate-200 p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-800">
                              {activity.description || "Sin descripción"}
                            </p>
                            <p className="text-sm text-slate-500 mt-1">
                              {activity.start_time || "--:--"} →{" "}
                              {activity.end_time || "--:--"}
                            </p>
                          </div>

                          <span className="shrink-0 bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-lg text-xs font-semibold">
                            {formatDuration(
                              Number(activity.duration_minutes || 0)
                            )}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">
                      No hay actividades registradas.
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-800 mb-3">
                  Descripción del trabajo
                </h3>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">
                  {viewingReport.work_description || "Sin descripción"}
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-800 mb-2">
                  Herramientas
                </h3>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">
                  {viewingReport.tools_used || "Sin herramientas registradas"}
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-800 mb-2">
                  Materiales / Insumos
                </h3>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">
                  {viewingReport.materials_used || "Sin materiales registrados"}
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-800 mb-2">
                  Observaciones
                </h3>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">
                  {viewingReport.observations || "Sin observaciones"}
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-800 mb-3">
                  Evidencias
                </h3>

                {viewingReport.images?.length ? (
                  <>
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm text-slate-500">
                        {viewingReport.images.length} fotografía(s)
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          downloadReportImages(
                            viewingReport.images,
                            viewingReport._id || viewingReport.id || "trabajo"
                          )
                        }
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        <Upload className="h-4 w-4 rotate-180" />
                        Descargar todas
                      </button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {viewingReport.images.map((img, index) => {
                      const imageUrl =
                        typeof img === "string" && img.startsWith("/uploads/")
                          ? `${window.location.origin}${img}`
                          : img;

                      return (
                        <button
                          key={`${index}-${imageUrl}`}
                          type="button"
                          onClick={() => {
                            const imageUrls = viewingReport.images.map((item) =>
                              typeof item === "string" && item.startsWith("/uploads/")
                                ? `${window.location.origin}${item}`
                                : item
                            );
                            setSelectedImages(imageUrls);
                            setCurrentImageIndex(index);
                            setSelectedImage(imageUrls[index]);
                          }}
                          className="group relative block overflow-hidden rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          title={`Ver fotografía ${index + 1}`}
                        >
                          <img
                            src={imageUrl}
                            alt={`Evidencia ${index + 1}`}
                            loading="lazy"
                            className="w-full h-40 object-cover transition-transform duration-200 group-hover:scale-105"
                          />
                          <span className="absolute inset-x-0 bottom-0 bg-black/60 px-2 py-1.5 text-xs font-medium text-white">
                            <Eye className="mr-1 inline h-3.5 w-3.5" />
                            Ver fotografía {index + 1}
                          </span>
                        </button>
                      );
                    })}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">
                    No hay fotografías.
                  </p>
                )}
              </div>

              {viewingReport.supervisor_comment && (
                <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                  <h3 className="font-semibold text-blue-800 mb-2">
                    Comentario del supervisor
                  </h3>
                  <p className="text-sm text-blue-700 whitespace-pre-wrap">
                    {viewingReport.supervisor_comment}
                  </p>
                </div>
              )}

            </div>

            <div className="border-t bg-white p-4 flex flex-wrap justify-end gap-2">
              {user?.permissions?.work_reports?.includes('approve') &&
                !viewingReport.reviewed && (
                  <button
                    type="button"
                    onClick={() => {
                      setViewingReport(null);
                      openReviewDialog(viewingReport.id);
                    }}
                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                  >
                    <ClipboardCheck className="w-4 h-4" />
                    Revisar
                  </button>
                )}

              <button
                type="button"
                onClick={() => {
                  editWorkReport(viewingReport);
                  setViewingReport(null);
                }}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                <Pencil className="w-4 h-4" />
                Editar
              </button>

              <button
                type="button"
                onClick={() => exportWorkReport(viewingReport)}
                className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                <FileText className="w-4 h-4" />
                PDF
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog
  open={reviewDialogOpen}
  onOpenChange={setReviewDialogOpen}
>
  <DialogContent className="max-w-lg">
    <DialogHeader>
      <DialogTitle>Revisar reporte</DialogTitle>
    </DialogHeader>

    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-2">
          Observación del supervisor
        </label>

        <textarea
          value={reviewData.comment}
          onChange={(e) =>
            setReviewData({
              ...reviewData,
              comment: e.target.value
            })
          }
          rows={5}
          className="w-full border rounded p-3"
          placeholder="Escriba una observación (opcional)..."
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => setReviewDialogOpen(false)}
        >
          Cancelar
        </Button>

        <Button onClick={confirmReview}>
          Confirmar revisión
        </Button>
      </div>
    </div>
  </DialogContent>
</Dialog>
      <Dialog
  open={!!selectedImage}
  onOpenChange={() => setSelectedImage(null)}
>
  <DialogContent className="max-w-5xl p-2 bg-black border-0">
    <div className="relative flex items-center justify-center">

  {currentImageIndex > 0 && (
    <button
      type="button"
      onClick={showPreviousImage}
      className="absolute left-2 z-10 bg-black/60 text-white rounded-full w-10 h-10 hover:bg-black"
    >
      ◀
    </button>
  )}

  {selectedImage && (
    <img
      src={selectedImage?.startsWith("/uploads/") ? `${process.env.REACT_APP_BACKEND_URL}${selectedImage}` : selectedImage}
      alt="Vista previa"
      className="w-full max-h-[85vh] object-contain rounded"
    />
  )}

  {currentImageIndex < selectedImages.length - 1 && (
    <button
      type="button"
      onClick={showNextImage}
      className="absolute right-2 z-10 bg-black/60 text-white rounded-full w-10 h-10 hover:bg-black"
    >
      ▶
    </button>
  )}

  <div className="absolute bottom-3 bg-black/70 text-white px-3 py-1 rounded text-sm">
    {currentImageIndex + 1} de {selectedImages.length}
  </div>

</div>

<div className="flex flex-wrap justify-center gap-3 bg-slate-950 p-3 rounded-b-lg">
  <a
    href={
      selectedImage?.startsWith("/uploads/")
        ? `${window.location.origin}${selectedImage}`
        : selectedImage || "#"
    }
    download={`evidencia-${currentImageIndex + 1}.jpg`}
    target="_blank"
    rel="noreferrer"
    className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-200"
  >
    <Upload className="h-4 w-4 rotate-180" />
    Descargar fotografía
  </a>

  <button
    type="button"
    onClick={async () => {
      const imageUrl = selectedImage?.startsWith("/uploads/")
        ? `${window.location.origin}${selectedImage}`
        : selectedImage;

      if (!imageUrl) return;

      try {
        if (!navigator.share || !navigator.canShare) {
          toast.error("Este navegador no permite compartir archivos directamente. Descarga la foto y adjúntala manualmente.");
          return;
        }

        const response = await fetch(imageUrl);
        if (!response.ok) {
          throw new Error(`Error HTTP ${response.status} al obtener la fotografía`);
        }

        const blob = await response.blob();
        const mimeType = blob.type || "image/jpeg";
        const extension = mimeType.includes("png")
          ? "png"
          : mimeType.includes("webp")
            ? "webp"
            : mimeType.includes("gif")
              ? "gif"
              : "jpg";

        const file = new File(
          [blob],
          `evidencia-${currentImageIndex + 1}.${extension}`,
          { type: mimeType }
        );

        if (!navigator.canShare({ files: [file] })) {
          toast.error("El dispositivo no admite compartir este archivo. Descarga la foto y adjúntala manualmente.");
          return;
        }

        await navigator.share({
          title: "Evidencia del reporte de trabajo",
          files: [file]
        });
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Error compartiendo archivo de evidencia:", error);
          toast.error("No se pudo compartir el archivo. Verifica la conexión e inténtalo nuevamente.");
        }
      }
    }}
    className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
  >
    <Upload className="h-4 w-4" />
    Compartir archivo
  </button>
</div>

  </DialogContent>
</Dialog>
    </div>
  );
};

export default WorkReports;